import { getRedirectUri } from "@/config/auth0";
import { Auth0ClientOptions } from "@auth0/auth0-spa-js/src/global";
import { Auth0Client } from "@auth0/auth0-spa-js";
import { ApiClient } from "@/api";
import User from "@/models/User";
import store from "@/store";
import { MutationTypes } from "@/store/modules/user";
import * as Sentry from "@sentry/vue";
import { initializeAmplitude } from "@/services/amplitude";
import toastProgrammatic from "@/toast/ToastProgrammatic";
import { runCallbackWithExclusiveLock } from "@/services/semaphore";
import PromiseExecutor from "@/types/promise";
import { LocalStorageHelper } from "@/utils/LocalStorageHelper";
import { CUBE_JS_TOKEN_STORAGE_KEY } from "@/reports/ReportModel";
import VueRouter from "vue-router";
import { getStaticConfiguration } from "@/config/app";
import { defaultNavigationErrorHandler } from "@/utils/Error";
import { isAuthTimeoutError, isSessionExpiredError } from "@/utils/AuthErrors";
import { reportAuthTimeout } from "@/services/authTimeoutReport";

export type OnReadyEvent = {
  token: string;
  client: Auth0Client;
};
export type OnUserAvailableEvent = { user: User | null; client: Auth0Client };

let auth0client: Auth0Client | null = null;

let _onReadyExecutors: PromiseExecutor<OnReadyEvent>;
export const onReadyPromise = new Promise<OnReadyEvent>((resolve, reject) => {
  _onReadyExecutors = { resolve, reject };
});

let _onUserAvailableExecutors: PromiseExecutor<OnUserAvailableEvent>;
export const onUserAvailablePromise = new Promise<OnUserAvailableEvent>(
  (resolve, reject) => {
    _onUserAvailableExecutors = { resolve, reject };
  }
);
// The router only subscribes once onReadyPromise resolves, so this may reject with no listener
onUserAvailablePromise.catch(() => undefined);

const getAuth0ClientLockHandle = Symbol("Auth0Client");
export async function getAuth0Client(): Promise<Auth0Client> {
  return runCallbackWithExclusiveLock(getAuth0ClientLockHandle, async () => {
    if (auth0client === null) {
      const frontendConfiguration = await getStaticConfiguration();

      const options: Auth0ClientOptions = {
        authorizationParams: {
          audience: frontendConfiguration.AUTH0_AUDIENCE,
          redirect_uri: getRedirectUri(),
        },
        useRefreshTokens: false,
        domain: frontendConfiguration.AUTH0_DOMAIN,
        clientId: frontendConfiguration.AUTH0_CLIENT_ID,
        cacheLocation: "memory",
      };

      auth0client = new Auth0Client(options);
    }

    return auth0client;
  });
}

export async function getTokenSilentlyAndTriggerReadyPromise(
  client: Auth0Client
): Promise<string> {
  const token = await client.getTokenSilently();

  _onReadyExecutors.resolve({
    token,
    client,
  });

  return token;
}

export async function runLoginCallbackSequence(
  client: Auth0Client,
  router: VueRouter
): Promise<void> {
  try {
    await getTokenSilentlyAndTriggerReadyPromise(client);

    const response = await ApiClient.Auth.me();

    const user = Object.assign(new User(), response.data);

    store.commit(`user/${MutationTypes.SetUser}`, user);

    const frontendConfiguration = await getStaticConfiguration();

    if (frontendConfiguration.SENTRY_DSN) {
      Sentry.setTag("user_name", `${user.name} (${user.id})`);
      Sentry.setUser({
        id: String(user.id),
        name: user.name,
        email: user.name,
        teams: user.teams.map((t) => `${t.id}-${t.name}`),
      });
    }

    initializeAmplitude(user, frontendConfiguration.AMPLITUDE_KEY);

    _onUserAvailableExecutors.resolve({
      user,
      client,
    });
  } catch (error) {
    // The router guard falls back to the login page (saving the requested URL) only when these reject
    _onReadyExecutors.reject(error);
    _onUserAvailableExecutors.reject(error);

    if (isAuthTimeoutError(error)) {
      await reportAuthTimeout(error, "get_token_silently");
    }

    if (isSessionExpiredError(error)) {
      // do nothing, don't report
    } else {
      toastProgrammatic.open({
        type: "is-danger",
        message: String(error),
        duration: 5000,
      });
    }
    // We are not logged in
    router.push({ name: "login" }).catch(defaultNavigationErrorHandler);
  }
}

export async function getAccessToken(): Promise<string> {
  await onReadyPromise;

  const client = await getAuth0Client();

  return client.getTokenSilently();
}

export async function logout(): Promise<void> {
  const client = await getAuth0Client();

  LocalStorageHelper.remove(CUBE_JS_TOKEN_STORAGE_KEY);

  await client.logout({
    logoutParams: {
      returnTo: `${window.location.protocol}//${window.location.host}`,
    },
  });
}
